import { useSyncExternalStore } from "react";
import type { CompoundLibEntry } from "./types/compound";
import type { FamilyKey } from "./types/flavor";
import type { FlavorFormulaItemDto } from "@/services/formula_api";
import type { SubstanceLensInfo } from "@/utils/types/substance";

// Runtime registry of substances fetched from the server. Keys are the
// namespaced "compound:<id>" / "food:<id>" strings that the server emits as
// `compoundId` on each item — the client treats these as opaque ids, and the
// FlaskPanel/FlaskRow `compoundById` resolver falls back to this registry
// when the mock COMPOUND_LIB doesn't contain a match.
//
// Plus a `lens` slot — extended for the Current-Flask server-data work (spec
// §8.2). FlaskPanel/FlaskRow read lens through lookupServerLens; a
// useRegistryVersion() hook lets components re-render when fresh data lands.
type CachedSubstance = CompoundLibEntry & { lens?: SubstanceLensInfo };
const registry = new Map<string, CachedSubstance>();

// Version counter + subscriber set for components that need to re-render
// when the registry is updated (e.g. FlaskPanel's lens grouping useMemo).
let registryVersion = 0;
const versionListeners = new Set<() => void>();
function bump(): void {
  registryVersion += 1;
  for (const cb of versionListeners) cb();
}

// Substances we know nothing real about default to "neutral" + "compound"
// kind. These choices are intentionally cheap — the design will not regress
// because FlaskRow uses --fam-neutral and a plain (non-italic) name when the
// family is neutral / kind is compound.
const FAMILY_FALLBACK: FamilyKey = "neutral";

function dtoToEntry(item: FlavorFormulaItemDto): CompoundLibEntry {
  // Foods get italic display via `kind: "substance"`, mirroring the existing
  // mock substance entries. Compounds default to plain non-substance. The
  // category attribute is unrelated to display kind — both FLAVOR and
  // FIXED_INGREDIENT items can be either compound or food.
  const kind: CompoundLibEntry["kind"] =
    item.substanceKind === "food" ? "substance" : "compound";
  return {
    id: item.compoundId,
    name: item.substanceName ?? `${item.substanceKind} #${item.substanceId}`,
    cas: item.substanceCas ?? "",
    family: FAMILY_FALLBACK,
    kind,
    desc: "",
    threshold: 0,
  };
}

/**
 * Lighter-weight registration for ingredients that arrive via the
 * Auto-Formulate SSE payload (which carries fewer fields than a
 * flavor-formula DTO). Same de-duplication / placeholder-patching rules
 * as `registerServerCompounds`.
 */
export function registerAutoFormulateIngredients(
  items: Array<{
    ndbTable: "public.compound" | "public.food";
    rowid: number;
    name?: string | null;
    cas?: string | null;
  }>,
): void {
  let changed = false;
  for (const it of items) {
    const substanceKind = it.ndbTable === "public.food" ? "food" : "compound";
    const compoundId = `${substanceKind}:${it.rowid}`;
    const name =
      it.name && it.name.trim().length > 0
        ? it.name
        : `${substanceKind} #${it.rowid}`;
    if (!registry.has(compoundId)) {
      registry.set(compoundId, {
        id: compoundId,
        name,
        cas: it.cas ?? "",
        family: FAMILY_FALLBACK,
        kind: substanceKind === "food" ? "substance" : "compound",
        desc: "",
        threshold: 0,
      });
      changed = true;
      continue;
    }
    // Patch a placeholder name when a real one finally arrives.
    const prev = registry.get(compoundId)!;
    const patchedName =
      it.name &&
      (prev.name.startsWith("compound #") || prev.name.startsWith("food #"))
        ? it.name
        : prev.name;
    const patchedCas = !prev.cas && it.cas ? it.cas : prev.cas;
    if (patchedName !== prev.name || patchedCas !== prev.cas) {
      registry.set(compoundId, { ...prev, name: patchedName, cas: patchedCas });
      changed = true;
    }
  }
  if (changed) bump();
}

export function registerServerCompounds(items: FlavorFormulaItemDto[]): void {
  let changed = false;
  for (const it of items) {
    if (!registry.has(it.compoundId)) {
      registry.set(it.compoundId, dtoToEntry(it));
      changed = true;
      continue;
    }
    // Existing entry — patch in any newly-resolved name / cas. The substance
    // kind never changes for a given (kind, id) pair so we don't refresh it.
    //
    // Real names are intentionally NOT overwritten within a session — the
    // registry is a within-render-cycle cache, and a full page reload will
    // re-register from fresh data. Only placeholder names ("compound #N" /
    // "food #N", produced when substanceName arrived as null) get patched
    // when a real name later arrives.
    const prev = registry.get(it.compoundId)!;
    const patchedName =
      (prev.name.startsWith("compound #") || prev.name.startsWith("food #")) &&
      it.substanceName
        ? it.substanceName
        : prev.name;
    const patchedCas = !prev.cas && it.substanceCas ? it.substanceCas : prev.cas;
    if (patchedName !== prev.name || patchedCas !== prev.cas) {
      registry.set(it.compoundId, { ...prev, name: patchedName, cas: patchedCas });
      changed = true;
    }
  }
  if (changed) bump();
}

// First-writer-wins merge. A compound's intrinsic lens info (family,
// chemClass, primaryRole, …) is stable across the session, so once any
// caller has registered a non-null value for a field, subsequent
// registrations from a different endpoint must NOT overwrite it.
//
// Background — two server endpoints emit lens for the same compound and
// they don't agree on shape:
//   - ingredient-search returns technical_roles in Postgres-yielded order;
//     family.code slugified ("animal-musky") with the search-side default
//     color.
//   - formulas/list uses substanceLens.ts which sorts technical_roles via
//     a global rank cache; family.code raw ("Animal & Musky") with the
//     lens-side default color.
// Without first-writer-wins, every refetch flipped primaryRole + family
// between the two shapes — visible in the FlaskPanel as items jumping
// between technical-role buckets ("ingredient X disappears" was actually
// "ingredient X moved bucket") and family color bands swapping between
// the two endpoints' palettes ("RED/GRAY defaulting" was the swap).
//
// Null-existing fields still receive the incoming value — that lets the
// second registration fill in gaps the first couldn't resolve (e.g.
// search returns family=null because the goodscents join missed for
// this compound; formulas can fill it in).
function mergeLens(
  existing: SubstanceLensInfo | undefined,
  incoming: SubstanceLensInfo,
): SubstanceLensInfo {
  if (!existing) return incoming;
  return {
    primaryRole:    existing.primaryRole    ?? incoming.primaryRole,
    technicalRoles:
      existing.technicalRoles.length > 0 ? existing.technicalRoles : incoming.technicalRoles,
    solubility:     existing.solubility     ?? incoming.solubility,
    chemClass:      existing.chemClass      ?? incoming.chemClass,
    family:         existing.family         ?? incoming.family,
    flavorLabels:
      existing.flavorLabels.length > 0 ? existing.flavorLabels : incoming.flavorLabels,
  };
}

// Cheap identity check — `family` carries a tuple of three strings so a
// shallow ref check would always say "changed" on a server refetch even
// when nothing useful differs. Comparing the structural fields gates the
// registry bump.
function lensEqual(a: SubstanceLensInfo, b: SubstanceLensInfo): boolean {
  if (a === b) return true;
  if (a.primaryRole !== b.primaryRole) return false;
  if (a.solubility  !== b.solubility)  return false;
  if (a.chemClass   !== b.chemClass)   return false;
  if (a.technicalRoles.length !== b.technicalRoles.length) return false;
  for (let i = 0; i < a.technicalRoles.length; i++) {
    if (a.technicalRoles[i] !== b.technicalRoles[i]) return false;
  }
  if (a.flavorLabels.length !== b.flavorLabels.length) return false;
  for (let i = 0; i < a.flavorLabels.length; i++) {
    if (a.flavorLabels[i] !== b.flavorLabels[i]) return false;
  }
  const af = a.family;
  const bf = b.family;
  if (af === bf) return true;
  if (!af || !bf) return false;
  return af.code === bf.code && af.label === bf.label && af.color === bf.color;
}

// Lens info comes from two callers (ingredient-search + formula list).
// Both pass the real name + cas when they have them, so the placeholder
// branch below only triggers if a caller registers lens before any name
// info has arrived for that substance (rare). Merging happens in two
// modes: when an entry already exists, patch in any provided name/cas
// (replacing the placeholder "compound #N" or empty cas); when the
// substance hasn't been seen yet, create the entry with real name/cas
// if provided, else a placeholder so FlaskPanel doesn't drop the row.
export function registerServerLens(
  rows: ReadonlyArray<{
    compoundId: string;
    lens: SubstanceLensInfo;
    name?: string;
    cas?: string | null;
    kind?: "compound" | "food";
  }>,
): void {
  if (rows.length === 0) return;
  let changed = false;
  for (const r of rows) {
    const existing = registry.get(r.compoundId);
    if (existing) {
      const mergedLens = mergeLens(existing.lens, r.lens);
      const isPlaceholderName =
        existing.name.startsWith("compound #") || existing.name.startsWith("food #");
      const nextName = r.name && isPlaceholderName ? r.name : existing.name;
      const nextCas  = r.cas && !existing.cas ? r.cas : existing.cas;
      const lensSame = existing.lens !== undefined && lensEqual(existing.lens, mergedLens);
      // Skip the registry write entirely when nothing changed — saves a
      // bump() and the consequent re-render of every FlaskRow / registry
      // subscriber. Important for the autosave path where useFlavorFormulas
      // refetches and re-registers every row with identical lens data.
      if (lensSame && nextName === existing.name && nextCas === existing.cas) continue;
      registry.set(r.compoundId, {
        ...existing,
        name: nextName,
        cas:  nextCas,
        lens: mergedLens,
      });
      changed = true;
    } else {
      const colon = r.compoundId.indexOf(":");
      const kindPart =
        r.kind ?? (colon > 0 ? (r.compoundId.slice(0, colon) as "compound" | "food") : "compound");
      const idPart = colon > 0 ? r.compoundId.slice(colon + 1) : "?";
      registry.set(r.compoundId, {
        id:        r.compoundId,
        name:      r.name ?? `${kindPart} #${idPart}`,
        cas:       r.cas ?? "",
        family:    FAMILY_FALLBACK,
        kind:      kindPart === "food" ? "substance" : "compound",
        desc:      "",
        threshold: 0,
        lens:      r.lens,
      });
      changed = true;
    }
  }
  if (changed) bump();
}

export function lookupServerCompound(
  id: string,
): CompoundLibEntry | undefined {
  return registry.get(id);
}

export function lookupServerLens(id: string): SubstanceLensInfo | null {
  return registry.get(id)?.lens ?? null;
}

// Subscribe to registry updates so components re-render when fresh
// data lands (e.g. FlaskPanel's lens grouping useMemo).
export function useRegistryVersion(): number {
  return useSyncExternalStore(
    (cb) => {
      versionListeners.add(cb);
      return () => versionListeners.delete(cb);
    },
    () => registryVersion,
    () => registryVersion,
  );
}

// Test-only accessor. Not used in production code.
export function _resetServerCompoundsForTest(): void {
  registry.clear();
  registryVersion = 0;
}
